// @ts-nocheck
/**
 * Error Handling Utilities for Frontend
 * Provides consistent error processing and user messaging
 */

import { AxiosError } from 'axios';
import {
  ApiErrorResponse,
  ApiResponse,
  ErrorType,
  ErrorCode,
  ERROR_MESSAGES,
  FieldError,
  isApiErrorResponse,
  createErrorResponse
} from '../types/api';

// Language preference type
export type Language = 'th' | 'en';

// Error display options
export interface ErrorDisplayOptions {
  language?: Language;
  showDetails?: boolean;
  showFieldErrors?: boolean;
  showRequestId?: boolean;
}

// Processed error for UI display
export interface ProcessedError {
  title: string;
  message: string;
  details?: string;
  fieldErrors?: FieldError[];
  requestId?: string;
  type: ErrorType;
  code: ErrorCode;
  isRetryable: boolean;
  suggestedAction?: string;
}

/**
 * Extract error information from various error sources
 */
export function extractErrorInfo(
  error: unknown,
  options: ErrorDisplayOptions = {}
): ProcessedError {
  const { language = 'th' } = options;

  // Handle API error responses
  if (isApiErrorResponse(error)) {
    return processApiErrorResponse(error, options);
  }

  // Handle Axios errors
  if (error?.isAxiosError || error?.response) {
    return processAxiosError(error as AxiosError, options);
  }

  // Handle network errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      title: 'การเชื่อมต่อหมดเวลา',
      message: ERROR_MESSAGES[ErrorCode.NETWORK_TIMEOUT][language],
      type: ErrorType.TIMEOUT_ERROR,
      code: ErrorCode.NETWORK_TIMEOUT,
      isRetryable: true,
      suggestedAction: 'กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่อีกครั้ง'
    };
  }

  // Handle network connectivity errors
  if (error?.code === 'ERR_NETWORK' || !navigator.onLine) {
    return {
      title: 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต',
      message: 'กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตของคุณ',
      type: ErrorType.NETWORK_ERROR,
      code: ErrorCode.NETWORK_TIMEOUT,
      isRetryable: true,
      suggestedAction: 'ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่'
    };
  }

  // Handle generic JavaScript errors
  if (error instanceof Error) {
    return {
      title: 'เกิดข้อผิดพลาด',
      message: error.message || ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR][language],
      type: ErrorType.SERVER_ERROR,
      code: ErrorCode.UNKNOWN_ERROR,
      isRetryable: false,
      details: options.showDetails ? error.stack : undefined
    };
  }

  // Fallback for unknown errors
  return {
    title: 'เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
    message: ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR][language],
    type: ErrorType.SERVER_ERROR,
    code: ErrorCode.UNKNOWN_ERROR,
    isRetryable: false
  };
}

/**
 * Process API error response
 */
function processApiErrorResponse(
  errorResponse: ApiErrorResponse,
  options: ErrorDisplayOptions
): ProcessedError {
  const { language = 'th' } = options;
  const { error } = errorResponse;

  const title = getErrorTitle(error.type, language);

  // Prioritize error.details over error.message for more specific error messages
  // This allows backend to send generic message + specific details
  const message = error.details
    ? error.details
    : (language === 'en' && error.message_en
      ? error.message_en
      : error.message);

  return {
    title,
    message,
    details: options.showDetails ? JSON.stringify(error.details, null, 2) : undefined,
    fieldErrors: options.showFieldErrors ? error.field_errors : undefined,
    requestId: options.showRequestId ? error.request_id : undefined,
    type: error.type,
    code: error.code as ErrorCode,
    isRetryable: isRetryableError(error.type),
    suggestedAction: getSuggestedAction(error.type, language)
  };
}

/**
 * Process Axios error
 */
function processAxiosError(
  axiosError: AxiosError,
  options: ErrorDisplayOptions
): ProcessedError {
  const { language = 'th' } = options;

  // Check if response contains API error format
  if (axiosError.response?.data && isApiErrorResponse(axiosError.response.data)) {
    return processApiErrorResponse(axiosError.response.data, options);
  }

  // Handle HTTP status codes
  const status = axiosError.response?.status;
  const statusText = axiosError.response?.statusText;
  const responseData = axiosError.response?.data;

  // Extract error message from backend response
  // Priority: detail field (FastAPI) > error.message > message field > generic message
  const extractBackendMessage = (): string | undefined => {
    if (typeof responseData === 'object' && responseData !== null) {
      // Check for FastAPI 'detail' field
      if (responseData.detail) {
        return typeof responseData.detail === 'string'
          ? responseData.detail
          : JSON.stringify(responseData.detail);
      }
      // Check for nested 'error.message' field (API error response format)
      if (responseData.error && typeof responseData.error === 'object' && responseData.error.message) {
        return responseData.error.message;
      }
      // Check for top-level 'message' field
      if (responseData.message) {
        return responseData.message;
      }
    }
    return undefined;
  };

  const backendMessage = extractBackendMessage();

  switch (status) {
    case 400:
      return {
        title: 'ข้อมูลไม่ถูกต้อง',
        message: backendMessage || ERROR_MESSAGES[ErrorCode.INVALID_FORMAT][language],
        type: ErrorType.VALIDATION_ERROR,
        code: ErrorCode.INVALID_FORMAT,
        isRetryable: false,
        details: options.showDetails ? responseData : undefined
      };

    case 401:
      return {
        title: 'ไม่ได้รับอนุญาต',
        message: backendMessage || ERROR_MESSAGES[ErrorCode.INVALID_CREDENTIALS][language],
        type: ErrorType.AUTHENTICATION_ERROR,
        code: ErrorCode.INVALID_CREDENTIALS,
        isRetryable: false,
        suggestedAction: 'กรุณาเข้าสู่ระบบใหม่'
      };

    case 403:
      return {
        title: 'ไม่มีสิทธิ์เข้าถึง',
        message: ERROR_MESSAGES[ErrorCode.INSUFFICIENT_PERMISSIONS][language],
        type: ErrorType.AUTHORIZATION_ERROR,
        code: ErrorCode.INSUFFICIENT_PERMISSIONS,
        isRetryable: false
      };

    case 404:
      return {
        title: 'ไม่พบข้อมูล',
        message: backendMessage || ERROR_MESSAGES[ErrorCode.COMPANY_NOT_FOUND][language],
        type: ErrorType.NOT_FOUND_ERROR,
        code: ErrorCode.COMPANY_NOT_FOUND,
        isRetryable: false
      };

    case 422:
      return {
        title: 'ข้อมูลไม่ถูกต้อง',
        message: backendMessage || ERROR_MESSAGES[ErrorCode.INVALID_FORMAT][language],
        type: ErrorType.VALIDATION_ERROR,
        code: ErrorCode.INVALID_FORMAT,
        isRetryable: false,
        details: options.showDetails ? responseData : undefined
      };

    case 429:
      return {
        title: 'คำร้องขอมากเกินไป',
        message: ERROR_MESSAGES[ErrorCode.RATE_LIMIT_EXCEEDED][language],
        type: ErrorType.RATE_LIMIT_ERROR,
        code: ErrorCode.RATE_LIMIT_EXCEEDED,
        isRetryable: true,
        suggestedAction: 'กรุณารอสักครู่แล้วลองใหม่'
      };

    case 500:
    case 502:
    case 503:
    case 504:
      return {
        title: 'ปัญหาเซิร์ฟเวอร์',
        message: backendMessage || ERROR_MESSAGES[ErrorCode.INTERNAL_SERVER_ERROR][language],
        type: ErrorType.SERVER_ERROR,
        code: ErrorCode.INTERNAL_SERVER_ERROR,
        isRetryable: true,
        suggestedAction: 'กรุณาลองใหม่ในภายหลัง'
      };

    default:
      return {
        title: 'เกิดข้อผิดพลาด',
        message: statusText || ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR][language],
        type: ErrorType.SERVER_ERROR,
        code: ErrorCode.UNKNOWN_ERROR,
        isRetryable: false,
        details: options.showDetails ? `HTTP ${status}: ${statusText}` : undefined
      };
  }
}

/**
 * Get error title based on error type
 */
function getErrorTitle(errorType: ErrorType, language: Language): string {
  const titles = {
    [ErrorType.VALIDATION_ERROR]: { th: 'ข้อมูลไม่ถูกต้อง', en: 'Validation Error' },
    [ErrorType.AUTHENTICATION_ERROR]: { th: 'ไม่ได้รับอนุญาต', en: 'Authentication Error' },
    [ErrorType.AUTHORIZATION_ERROR]: { th: 'ไม่มีสิทธิ์เข้าถึง', en: 'Authorization Error' },
    [ErrorType.NOT_FOUND_ERROR]: { th: 'ไม่พบข้อมูล', en: 'Not Found' },
    [ErrorType.RATE_LIMIT_ERROR]: { th: 'คำร้องขอมากเกินไป', en: 'Rate Limit Exceeded' },
    [ErrorType.SERVER_ERROR]: { th: 'ปัญหาเซิร์ฟเวอร์', en: 'Server Error' },
    [ErrorType.NETWORK_ERROR]: { th: 'ปัญหาการเชื่อมต่อ', en: 'Network Error' },
    [ErrorType.TIMEOUT_ERROR]: { th: 'การเชื่อมต่อหมดเวลา', en: 'Timeout Error' },
    [ErrorType.DATABASE_ERROR]: { th: 'ปัญหาฐานข้อมูล', en: 'Database Error' },
    [ErrorType.BUSINESS_LOGIC_ERROR]: { th: 'กฎทางธุรกิจไม่อนุญาต', en: 'Business Logic Error' }
  };

  return titles[errorType]?.[language] || 'เกิดข้อผิดพลาด';
}

/**
 * Check if error is retryable
 */
function isRetryableError(errorType: ErrorType): boolean {
  const retryableTypes = [
    ErrorType.RATE_LIMIT_ERROR,
    ErrorType.SERVER_ERROR,
    ErrorType.NETWORK_ERROR,
    ErrorType.TIMEOUT_ERROR,
    ErrorType.DATABASE_ERROR
  ];

  return retryableTypes.includes(errorType);
}

/**
 * Get suggested action for error type
 */
function getSuggestedAction(errorType: ErrorType, language: Language): string | undefined {
  const actions = {
    [ErrorType.AUTHENTICATION_ERROR]: {
      th: 'กรุณาเข้าสู่ระบบใหม่',
      en: 'Please login again'
    },
    [ErrorType.AUTHORIZATION_ERROR]: {
      th: 'ติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์เข้าถึง',
      en: 'Contact administrator for access'
    },
    [ErrorType.RATE_LIMIT_ERROR]: {
      th: 'กรุณารอสักครู่แล้วลองใหม่',
      en: 'Please wait a moment and try again'
    },
    [ErrorType.SERVER_ERROR]: {
      th: 'กรุณาลองใหม่ในภายหลัง หากปัญหายังคงอยู่ติดต่อทีมสนับสนุน',
      en: 'Please try again later or contact support if the problem persists'
    },
    [ErrorType.NETWORK_ERROR]: {
      th: 'ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่',
      en: 'Check your internet connection and try again'
    },
    [ErrorType.TIMEOUT_ERROR]: {
      th: 'ตรวจสอบการเชื่อมต่ออินเทอร์เน็ตและลองใหม่',
      en: 'Check your connection and try again'
    }
  };

  return actions[errorType]?.[language];
}

/**
 * Format field errors for display
 */
export function formatFieldErrors(fieldErrors: FieldError[], language: Language = 'th'): string {
  if (!fieldErrors || fieldErrors.length === 0) {
    return '';
  }

  return fieldErrors
    .map(error => {
      const message = language === 'en' && error.message_en
        ? error.message_en
        : error.message;
      return `${error.field}: ${message}`;
    })
    .join('\n');
}

/**
 * Check if error indicates authentication failure
 */
export function isAuthenticationError(error: unknown): boolean {
  const processedError = extractErrorInfo(error);
  return processedError.type === ErrorType.AUTHENTICATION_ERROR;
}

/**
 * Check if error indicates authorization failure
 */
export function isAuthorizationError(error: unknown): boolean {
  const processedError = extractErrorInfo(error);
  return processedError.type === ErrorType.AUTHORIZATION_ERROR;
}

/**
 * Check if error should trigger a retry
 */
export function shouldRetry(error: unknown): boolean {
  const processedError = extractErrorInfo(error);
  return processedError.isRetryable;
}

/**
 * Get user-friendly error message for notifications
 */
export function getNotificationMessage(
  error: unknown,
  language: Language = 'th'
): string {
  const processedError = extractErrorInfo(error, { language });

  if (processedError.suggestedAction) {
    return `${processedError.message}\n\n${processedError.suggestedAction}`;
  }

  return processedError.message;
}

/**
 * Create client-side validation error
 */
export function createValidationError(
  fieldErrors: FieldError[]
): ApiErrorResponse {
  return createErrorResponse(
    ErrorCode.REQUIRED_FIELD_MISSING,
    ErrorType.VALIDATION_ERROR,
    'Client-side validation failed',
    fieldErrors
  );
}

/**
 * Log error for debugging (production-safe)
 */
export function logError(error: unknown, context?: Record<string, unknown>): void {
  const processedError = extractErrorInfo(error, { showDetails: true });

  const logData = {
    timestamp: new Date().toISOString(),
    error: processedError,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', logData);
  } else {
    // In production, send to logging service
    // Example: sendToLoggingService(logData);
    console.error('Error:', processedError.message);
  }
}

/**
 * Error boundary error handler
 */
export function handleErrorBoundary(error: Error, errorInfo: { componentStack: string }): void {
  const processedError = extractErrorInfo(error, { showDetails: true });

  logError(error, {
    errorBoundary: true,
    componentStack: errorInfo.componentStack,
    errorBoundaryType: 'React Error Boundary'
  });

  // Optionally send to error tracking service
  // Example: Sentry.captureException(error, { extra: errorInfo });
}

// Export utility functions
export {
  ErrorType,
  ErrorCode,
  ERROR_MESSAGES
} from '../types/api';